import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Plus, Pencil, Trash2, Eye, Download, LogOut, ArrowLeft, Sparkles, Mail } from "lucide-react";
import { api, getToken, setToken, clearToken, authHeaders, API, assetUrl } from "@/lib/api";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Login = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.login(password);
      setToken(r.data.token);
      onLogin();
      toast.success("Přihlášení úspěšné");
    } catch {
      toast.error("Nesprávné heslo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7] px-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-[#E5E0D8] bg-white p-10 shadow-sm" data-testid="admin-login-form">
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#C5A059]/10 text-[#C5A059]">
          <Lock size={22} />
        </div>
        <h1 className="mb-1 text-center font-heading text-2xl">Administrace</h1>
        <p className="mb-8 text-center text-sm text-[#5C5A55]">Zadejte heslo pro správu světů</p>
        <Input
          type="password"
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Heslo"
          className="mb-4"
        />
        <Button type="submit" data-testid="admin-login-button" disabled={loading || !password} className="w-full bg-[#1A1A1A] hover:bg-[#C5A059]">
          {loading ? "Přihlašuji…" : "Přihlásit se"}
        </Button>
        <Link to="/" className="mt-6 flex items-center justify-center gap-1 text-sm text-[#5C5A55] hover:text-[#C5A059]">
          <ArrowLeft size={14} /> Zpět na web
        </Link>
      </form>
    </div>
  );
};

const Dashboard = ({ onLogout }) => {
  const [worlds, setWorlds] = useState([]);
  const [messages, setMessages] = useState([]);

  const load = () => {
    api.listWorlds().then((r) => setWorlds(r.data)).catch(() => {});
    axios.get(`${API}/contacts`, { headers: authHeaders() }).then((r) => setMessages(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addWorld = async () => {
    try {
      const r = await api.createWorld({ name: "Nový svět" });
      toast.success("Svět vytvořen");
      window.location.href = `/admin/svet/${r.data.id}`;
    } catch {
      toast.error("Vytvoření se nezdařilo");
    }
  };

  const removeWorld = async (w) => {
    if (!window.confirm(`Opravdu smazat svět „${w.name}"?`)) return;
    try {
      await api.deleteWorld(w.id);
      toast.success("Svět smazán");
      load();
    } catch {
      toast.error("Smazání se nezdařilo");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="flex items-center justify-between border-b border-[#E5E0D8] bg-white px-6 py-4 md:px-12">
        <div className="flex items-center gap-2 font-heading text-xl">
          <Sparkles size={20} className="text-[#C5A059]" /> Administrace světů
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="text-sm text-[#5C5A55] hover:text-[#C5A059]">Hlavní web</Link>
          <Button variant="outline" size="sm" data-testid="admin-logout-btn" onClick={onLogout}>
            <LogOut size={14} className="mr-1" /> Odhlásit
          </Button>
        </div>
      </nav>

      <div className="mx-auto max-w-6xl px-6 py-10 md:px-12">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl">Svatební světy</h1>
            <p className="text-sm text-[#5C5A55]">{worlds.length} světů · upravujte, generujte, přidávejte</p>
          </div>
          <Button data-testid="add-world-btn" onClick={addWorld} className="bg-[#1A1A1A] hover:bg-[#C5A059]">
            <Plus size={16} className="mr-1" /> Přidat svět
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-worlds-list">
          {worlds.map((w) => (
            <div key={w.id} className="overflow-hidden rounded-xl border border-[#E5E0D8] bg-white" data-testid={`admin-world-card-${w.slug}`}>
              <div className="relative h-36">
                <img src={assetUrl(w.preview_image)} alt={w.name} className="h-full w-full object-cover" style={{ backgroundColor: w.theme?.bg }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-4 text-white">
                  <div className="font-heading text-lg">{w.name}</div>
                  <div className="text-xs text-white/70">{w.couple?.name1} & {w.couple?.name2} · {w.wedding?.date}</div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 p-4">
                <Link to={`/admin/svet/${w.id}`} className="flex-1">
                  <Button size="sm" className="w-full bg-[#1A1A1A] hover:bg-[#C5A059]" data-testid={`edit-world-btn-${w.slug}`}>
                    <Pencil size={14} className="mr-1" /> Upravit
                  </Button>
                </Link>
                <Link to={`/svet/${w.slug}`}>
                  <Button size="sm" variant="outline" data-testid={`preview-world-btn-${w.slug}`}><Eye size={14} /></Button>
                </Link>
                <a href={api.zipUrl(w.id)} data-testid={`zip-world-btn-${w.slug}`}>
                  <Button size="sm" variant="outline"><Download size={14} /></Button>
                </a>
                <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-50" data-testid={`delete-world-btn-${w.slug}`} onClick={() => removeWorld(w)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-2xl">
            <Mail size={20} className="text-[#C5A059]" /> Zprávy z kontaktního formuláře ({messages.length})
          </h2>
          {messages.length === 0 ? (
            <p className="text-sm text-[#5C5A55]">Zatím žádné zprávy.</p>
          ) : (
            <div className="space-y-3" data-testid="contact-messages-list">
              {messages.map((m) => (
                <div key={m.id} className="rounded-xl border border-[#E5E0D8] bg-white p-5">
                  <div className="mb-1 flex flex-wrap items-center gap-3 text-sm">
                    <span className="font-medium">{m.name}</span>
                    <a href={`mailto:${m.email}`} className="text-[#C5A059]">{m.email}</a>
                    {m.phone && <a href={`tel:${m.phone}`} className="text-[#C5A059]" data-testid="message-phone">{m.phone}</a>}
                    <span className="text-xs text-[#5C5A55]">{new Date(m.created_at).toLocaleString("cs-CZ")}</span>
                  </div>
                  <p className="text-sm text-[#3a3a38]">{m.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    api.verify().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);

  if (authed === null) return <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7] text-[#5C5A55]">Načítám…</div>;
  if (!authed) return <Login onLogin={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => { clearToken(); setAuthed(false); }} />;
}
